import React, { forwardRef } from 'react';
import { MATERIAL_ICONS } from '../Icon/icons';
import { Size, SIZES } from '../../fundamentals/sizes/sizes';
import { DEFAULT_ICON_TYPE, Icon } from '../Icon/Icon';
import styled, { css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { IGutterProps } from '../../fundamentals/spaces/space';
import { IIconBaseProps } from '../../common/interfaces';
import {
  IButtonProps,
  IBaseButtonProps,
  DEFAULT_BUTTON_VARIANT,
  getIconColour,
  useButtonCss,
  isButtonLinkVariant,
  DEFAULT_BUTTON_MODE
} from '../Button/Button';
import { ButtonRoot } from '../Button/ButtonRoot';
import { ButtonMode } from '../Button/buttonVariants';

export type IconButtonSize = typeof SIZES.XX_SMALL | typeof SIZES.X_SMALL | typeof SIZES.SMALL | typeof SIZES.MEDIUM | typeof SIZES.LARGE;

export interface IIconButtonProps extends Omit<IBaseButtonProps, 'children'>, IGutterProps, IIconBaseProps, Pick<IButtonProps, 'variant' | 'mode'> {
  size?: IconButtonSize;
}

export const iconButtonIconSizes: Record<IconButtonSize, Size> = {
  [SIZES.XX_SMALL]: SIZES.MICRO,
  [SIZES.X_SMALL]: SIZES.MICRO,
  [SIZES.SMALL]: SIZES.XXX_SMALL,
  [SIZES.MEDIUM]: SIZES.X_SMALL,
  [SIZES.LARGE]: SIZES.X_SMALL
};

export const useIconButtonCss = () => css<{ size?: IconButtonSize | Size }>`
  height: ${({ theme, size }) => theme.sizes[size!]};
  width: ${({ theme, size }) => theme.sizes[size!]};
  min-height: ${({ theme, size }) => theme.sizes[size!]};
  min-width: ${({ theme, size }) => theme.sizes[size!]};
  border-radius: 50%;
  line-height: 0;
`;

const IconButtonBase = styled(ButtonRoot)<Omit<IIconButtonProps, 'size' | 'mode'> & { size?: IconButtonSize | Size; styleMode: ButtonMode }>`
  ${useIconButtonCss};
  ${useButtonCss};
`;

export const IconButton = forwardRef<HTMLButtonElement, IIconButtonProps>(
  (
    {
      size = SIZES.MEDIUM,
      variant = DEFAULT_BUTTON_VARIANT,
      disabled,
      icon = MATERIAL_ICONS.Star,
      iconType = DEFAULT_ICON_TYPE,
      mode = DEFAULT_BUTTON_MODE,
      onClick,
      ...rest
    },
    ref
  ) => {
    const { generateTestId } = useTestId(rest, `IconButton`);

    const colour = getIconColour({ variant: variant, mode, disabled });
    const handleOnclick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      e.currentTarget.blur();
    };

    return (
      <IconButtonBase
        ref={ref}
        {...generateTestId(variant)}
        size={isButtonLinkVariant(variant) ? iconButtonIconSizes[size] : size}
        variant={variant}
        disabled={disabled}
        onClick={onClick && handleOnclick}
        styleMode={mode}
        {...rest}
      >
        <Icon colour={colour} size={iconButtonIconSizes[size]} type={iconType} name={icon} />
      </IconButtonBase>
    );
  }
);
